import React, { useState, useCallback, useEffect } from "react";
import dayjs from "dayjs";
import SinglePageContainer from "../../../Common/SinglePageContainer";
import {
  Button,
  Segmented,
  message,
  Spin,
  Dropdown,
  MenuProps,
  Typography,
  Badge,
  Col,
  Row,
} from "antd";
import {
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EllipsisOutlined,
  ExclamationCircleFilled,
  EyeOutlined,
  HomeOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { Survey } from "../../../../types/survey";
import { SurveyBuilder } from "../../../Common/Survey/SurveyBuilder";
import {
  SurveyStatus,
  useSurveyCreateFull,
  useSurveyDelete,
  useSurveyGetById,
  useSurveyUpdate,
  useTempateDownload,
  useTemplateCopy,
  useTemplateCreate,
  useTemplateDelete,
  useTemplateGetById,
  useTemplateUpdate,
} from "../../../../hooks/api/surveys";
import { NavLink, useLocation, useNavigate, useParams } from "react-router";
import { adaptApiSurvey } from "../../../../utils/surveyAdapter";
import { useBreadcrumbs } from "../../../../context/breadcrumbs";
import { SurveyPreview } from "../../../Common/SurveyPreview";
import { useSimpleModal } from "../../../Common/Modal/Simple";

import styles from "./index.module.scss";
import { getSurveyStatusColor } from "..";
import { dateTimeFormat, dateTimeFormatRevert } from "../../../../contstant";
import {
  transformApiSurveyToApp,
  transformSurveyToApi,
} from "../../../../utils/transformSurveyToApi";
import { useMessageError, useMessageSuccess } from "../../../../hooks/common";
import { cloneSurveyWithNewIds } from "../../../../utils/cloneSurveyForTemplate";
import Loading from "../../../Common/Loading";
import StatusIndicator from "../../../Common/StatusIndicator";
import { useUnsavedChangesGuard } from "../../../../utils/useUnsavedChangesGuard";
import isEqual from "lodash/isEqual";
import { downloadFromAnchor } from "../../../../utils";

const { Text, Title } = Typography;

const createEmptySurvey = (): Survey => ({
  status: "draft",
  title: "",
  items: [],
});

export default function TemplateEditContent(): React.ReactNode | null {
  const [mode, setMode] = useState<"add" | "preview">("add");
  const { id } = useParams();
  const navigate = useNavigate();
  const { open, contextHolder } = useSimpleModal();
  const { setBreadcrumbs } = useBreadcrumbs();
  const templateGetById = useTemplateGetById(id);
  const templateUpdate = useTemplateUpdate();
  const templateDelete = useTemplateDelete(id);
  const surveyCreate = useSurveyCreateFull();
  const templateCopy = useTemplateCopy();
  const tempateDownload = useTempateDownload(id);
  const location = useLocation();
  const fromPage = location.state?.fromPage || "1";
  const [surveyData, setSurveyData] = useState<Survey>(createEmptySurvey());
  const [originalData, setOriginalData] = useState<Survey | null>(null);

  useEffect(() => {
    setBreadcrumbs([
      { title: <HomeOutlined />, href: "/" },
      { title: "Templates", href: "/templates" },
      { title: "Edit" },
    ]);
  }, []);

  const isDirty = originalData
    ? !isEqual(
      { ...surveyData, updatedAt: undefined },
      { ...originalData, updatedAt: undefined },
    )
    : false;

  useUnsavedChangesGuard(isDirty, () => { }, {
    open: (config) =>
      open({
        ...config,
        okButtonProps: { danger: true },
        icon: <ExclamationCircleFilled />,
      }),
  });

  useEffect(() => {
    if (
      templateGetById.data &&
      !templateGetById.loading &&
      !templateGetById.error
    ) {
      const transformed = transformApiSurveyToApp(templateGetById.data);
      setSurveyData(transformed);
      setOriginalData(transformed);
    }
  }, [templateGetById.data, templateGetById.loading, templateGetById.error]);

  // useEffect(() => {
  //   if (templateUpdate.data) {
  //     navigate(`/templates?current=${fromPage}`);
  //   }
  // }, [templateUpdate, fromPage, navigate]);

  useEffect(() => {
    if (
      !templateDelete.error &&
      !templateDelete.loading &&
      templateDelete.data
    ) {
      navigate(`/templates?current=${fromPage}`);
    }
  }, [
    templateDelete.error,
    templateDelete.loading,
    templateDelete.data,
    fromPage,
    navigate,
  ]);

  const handleSurveyChange = useCallback((survey: Survey) => {
    setSurveyData(survey);
  }, []);

  const handleSave = useCallback(async () => {
    if (!surveyData.title.trim()) {
      message.error("Please enter template title");
      return;
    }
    const apiData = transformSurveyToApi({
      ...surveyData,
      status: "draft",
    });
    try {
      const res = await templateUpdate.fetch(apiData, id);
      if (res?.id) {
        message.success("Template saved");
      }
      setOriginalData(surveyData);
    } catch (err: any) {
      message.error(err?.message || "Failed to save template");
      console.error(err);
    }
  }, [surveyData, templateUpdate, id]);

  const handleSaveSurvey = useCallback(async () => {
    const surveyClone = cloneSurveyWithNewIds(surveyData);

    const apiData = transformSurveyToApi({
      ...surveyClone,
      status: "draft",
    });

    try {
      const res = await surveyCreate.fetch(apiData);

      if (res?.id) {
        message.success("Survey created");
      }
      setOriginalData(surveyData);
    } catch (err: any) {
      message.error(err?.message || "Failed to save survey");
    }
  }, [surveyData, surveyCreate]);

  const handleModeChange = useCallback((value: "add" | "preview") => {
    setMode(value);
  }, []);
  const items: MenuProps["items"] = [
    {
      key: "download",
      type: "item",
      icon: <DownloadOutlined />,
      label: "Download",
      onClick: () => {
        tempateDownload.fetch().then((response) => {
          if (!response) return;
          downloadFromAnchor(
            response,
            `${templateGetById.data?.title}`,
            "application/pdf",
          );
        });
      },
    },
    {
      key: "delete",
      type: "item",
      icon: <DeleteOutlined style={{ color: "red" }} />,
      label: <span style={{ color: "red" }}>Delete</span>,
      onClick: () => {
        open({
          icon: <ExclamationCircleFilled />,
          title: "Delete template?",
          content: (
            <span>
              Are you sure you want to delete this template? This action cannot
              be undone and the template will be permanently removed.
            </span>
          ),
          cancelText: "Cancel",
          okText: "Delete",
          okButtonProps: { danger: true },
          onOk: () => templateDelete.fetch(),
        });
      },
    },
  ];

  useMessageSuccess([templateUpdate], "Changes saved");
  useMessageSuccess([templateDelete], "Template deleted");
  useMessageError([templateUpdate]);
  useMessageError([surveyCreate]);
  useMessageSuccess([templateCopy], "Template copied");

  return (
    <SinglePageContainer
      title="Edit template"
      className="pagePadding"
      extra={
        <>
          <Button onClick={handleSave} loading={templateUpdate.loading}>
            Save
          </Button>
          <Button
            type="primary"
            onClick={handleSaveSurvey}
            loading={surveyCreate.loading}
          >
            Create survey
          </Button>
          <Button
            icon={<CopyOutlined />}
            onClick={async () => {
              const res = await templateCopy.fetch(undefined, `${id}/copy`);
              if (res) {
                navigate(`/templates/${res.id}/edit`);
              }
            }}
          />
          <Dropdown
            menu={{ items }}
            placement="bottomRight"
            trigger={["click"]}
          >
            <Button icon={<EllipsisOutlined />} />
          </Dropdown>
        </>
      }
      style={{ backgroundColor: "transparent" }}
    >
      <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
        <div style={{ width: 880 }}>
          <Segmented
            size="large"
            value={mode}
            onChange={(value) => handleModeChange(value as "add" | "preview")}
            options={[
              { label: "Edit", value: "add", icon: <EditOutlined /> },
              { label: "Preview", value: "preview", icon: <EyeOutlined /> },
            ]}
          />
          {mode === "add" ? (
            <div className={styles.editInfoWrapper}>
              <div className={styles.editInfo}>
                <Row gutter={[16, 0]}>
                  <Col span={6}>
                    <Text strong>Title</Text>
                  </Col>
                  <Col span={18}>
                    <Text>{surveyData.title}</Text>
                  </Col>
                  <Col span={6}>
                    <Text strong>Last updated</Text>
                  </Col>
                  <Col span={18}>
                    <Text>
                      {dayjs(surveyData.updatedAt).format(dateTimeFormatRevert)}
                    </Text>
                  </Col>
                  <Col span={6}>
                    <Text strong>Created by</Text>
                  </Col>
                  <Col span={18}>
                    <NavLink
                      to={`/users/${surveyData.user?.id}/edit`}
                      style={{ textDecoration: "underline" }}
                    >
                      {surveyData.user?.firstName} {surveyData.user?.lastName}
                    </NavLink>
                  </Col>
                </Row>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <div style={{ marginTop: 24 }}>
        {mode === "add" && (
          <SurveyBuilder
            isTemplate
            initialSurvey={surveyData}
            setInitialSurvey={setSurveyData}
            onChange={handleSurveyChange}
          />
        )}
        {mode === "preview" && (
          <SurveyPreview
            isTemplate
            survey={surveyData}
            readOnly={false}
            onSubmit={(answers) => console.log("Answers:", answers)}
          />
        )}
        {templateGetById.loading ||
          surveyCreate.loading ||
          templateCopy.loading ||
          templateUpdate.loading ? (
          <Loading absolute />
        ) : null}
      </div>
      {contextHolder}
    </SinglePageContainer>
  );
}
